import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, booleanAttribute, computed, forwardRef, input, linkedSignal, output } from '@angular/core';
import { Observable } from 'rxjs';

import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SortEvent } from 'primeng/api';
import { HisControlValueAccessor } from '../../../core/forms/his-control-value-accessor';
import { GenericValue } from '../../../core/model/generic-value';
import { HisOptionGroup, HisOptionXtd, isHisOption } from '../../../core/model/his-option';
import { isNonEmptyArray } from '../../../core/utils';

@Component( {
    selector: 'his-multiselect',
    templateUrl: './his-multiselect.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef( () => HisMultiselectComponent ),
            multi: true,
        },
    ],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
} )
export class HisMultiselectComponent<T extends HisOptionXtd | GenericValue = HisOptionXtd> extends HisControlValueAccessor {

    @Input() ddKey?: string;

    @Input() hisDdKey?: string; // Wert der hisDdKey - Direktive, um sie an Unterkomponenten weiterzureichen

    @Input() dataSource: string;

    @Input() inputId: string;

    /** Alternative property to use as the text for the selected-options-list labels */
    @Input() field: string;

    /** name of a custom search method in calling component @deprecated */
    @Input() customOptionsMethod: ( autocompleteQuery: string ) => Observable<HisOptionXtd[]>;


    @Input() label: string;

    /** minimum length of autocomplete input to trigger a search @deprecated effectively removed. use `limit` in case of performance issues */
    @Input() minLength = 1;
    /** maximum number of suggestions to show in autocomplete (null -> show all) */
    @Input() limit: number | null = null;
    /** show icon to open dropdown? (Default `true`) */
    @Input() showDropdownIcon = true;
    /** show null item in autocomplete select list? (Default `false`) @deprecated unused */
    @Input() showNullItem = false;

    /** Show button 'Auswahl löschen'? (Default `false`) */
    readonly showDeleteButton = input( false, { transform: booleanAttribute } );
    /** Show button 'Auswählen'/'Auswahl ändern'? (Default `false`) */
    readonly showSelectButton = input( false, { transform: booleanAttribute } );
    /** Enable editing/user input? (Default `true`) */
    readonly editMode = input( true );
    /** enable grouping of options in the select list (Default `false`) */
    readonly group = input( false, { transform: booleanAttribute } );
    /** Filter selected options from the suggestion list? (Default `false`) */
    readonly removeSelectedOptionsFromDropdown = input( false, { transform: booleanAttribute } );

    /** name of a custom image for the select button, e.g. "USER_ADD" */
    @Input() selectButtonImage: string = undefined;
    /** custom select button tooltip text (i18n keys should've been substituted already) */
    @Input() selectButtonTooltip: string = undefined;
	
    /** @deprecated if `sortMethod` is defined, it should be used, otherwise not. To keep compatibility, `sortMethod` wont be used if `customSort` is explicitly _false_ */
    @Input() customSort: boolean = undefined;
    
    /** @deprecated unused */
    @Output() selected: EventEmitter<HisOptionXtd> = new EventEmitter();
    /** Custom sort function for the suggestion list and overlay table */
    @Input() sortMethod!: (event: SortEvent) => void;
    
    
    private readonly DEFAULT_PAGE_SIZE = 10;
    /** the number of result rows per page in the overlay table (is read from and written to UserProfile) */
    @Input() resultRowsPerPage = this.DEFAULT_PAGE_SIZE;
    
    /** Maximale Anzahl von Zeile pro Seite: Zeilen pro Seite (Max:maxRows) */
    @Input() maxRows = 300;

    /** DEPRECATED! Use 'maxDisplayedSelections' instead. @deprecated */
    @Input() maxSelectedOptions: number = 10;
    /** Limit the number of selections displayed. Overflow will be combined to a single label. */
    // default to 10, similar to JSF
    @Input() maxDisplayedSelections = 10;

    protected readonly disabled = linkedSignal( () => !this.editMode() );

    /** The complete list of available options for the user to choose from. Can either be a flat collection of `HisOption` or grouped together as `HisOptionGroup`. */
    readonly suggestions = input<HisOptionXtd[] | HisOptionGroup[] | null, HisOptionXtd[] | HisOptionGroup[] | object[] | null>( null, { transform: (values) => this.transformToHisOptions(values) } );
    protected readonly optionList = linkedSignal( () => this.suggestions() );
    protected readonly flatOptionList = computed( () => this.toFlatOptionList( this.optionList() ) );

    /**
     * An initial selection that was previously saved can be supplied with this property.\
     * The `selectedOptionsChange` allows for 2-way-binding to a property or writable signal.\
     * `[(selectedOptions)]=mySelection`
     */
    readonly selectedOptions = input<T[]>( [] );
    /** Emits the current selection after it has been updated. */
    readonly selectedOptionsChange = output<T[]>();
    /** The transform would break the generic input for the his-multiselect-plam, so it is derived later instead of using the `transform` option. */
    protected readonly selectedOptionsTransformed = computed( () => this.selectedOptions()?.map( this.toHisOption.bind( this ) ) ?? [] );
    protected readonly selectedOptionsModel = linkedSignal<HisOptionXtd[]>( () => this.selectedOptionsTransformed() );

    writeValue( value: any[] ): void {
        this.selectedOptionsModel.set( value ?? [] );
    }

    setDisabledState( isDisabled: boolean ): void {
        if ( this.editMode() ) {
            // if editMode is false, the form should not override the disabled state
            this.disabled.set( isDisabled );
        }
    }

    /** @deprecated unused */
    onSearch( e: { query: string } ) {
        this.loadOptions();
    }

    loadOptions() {
        if ( this.customOptionsMethod ) {
            this.customOptionsMethod( '' ).subscribe( result => {
                if ( Array.isArray( result ) ) {
                    this.setOptionList( [...result] );
                }
            } );
        }
    }

    protected onSelectedOptionsChange( newSelectedOptions: HisOptionXtd[] ) {
        const sorted = this.sortSelectedOptions( newSelectedOptions );
        sorted.forEach( ( opt, idx ) => opt.sortorder = idx );

        this.onModelChange( sorted );
        this.onModelTouched();

        this.selectedOptionsModel.set( sorted );
        this.selectedOptionsChange.emit( sorted as any );
    }

    protected setOptionList( options: HisOptionXtd[] ) {
        this.optionList.set( options );
    }

    private sortSelectedOptions( options: HisOptionXtd[] ) {
        if ( this.sortMethod && this.customSort !== false ) {
            this.sortMethod( { data: options, order: 1, field: 'label' } );
            return options;
        }

        // default sorting fallback
        return options.sort( ( n1, n2 ) => {
            if ( n1.label > n2.label ) {
                return 1;
            }
            if ( n1.label < n2.label ) {
                return -1;
            }
            return 0;
        } );
    }

    private isGroup( options: any[] ): options is HisOptionGroup[] {
        return this.group();
    }

    private toFlatOptionList( options: HisOptionXtd[] | HisOptionGroup[] | null ): HisOptionXtd[] | null {
        if ( !options ) return null;
        if ( this.isGroup( options ) ) {
            return options.flatMap( group => group.items );
        }
        return options;
    }

    private transformToHisOptions( values: any[] | null ): any[] | null {
        if ( !isNonEmptyArray( values ) || ( isHisOption( values[0] ) && !this.field ) || this.group() ) {
            // if undefined/empty, already options & no field override, or grouped options, return as-is
            return values ?? null;
        }
        return values.map( this.toHisOption.bind( this ) );
    }

    protected toHisOption( value: any ): HisOptionXtd {
        return {
            ...value,
            id: String( value.id ),
            label: value[this.field] || value.label || value.title || value.defaulttext || value.text
        };
    }
}